import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Building2, FileText, Users as UsersIcon, HardHat, Tag, Loader2, Copy, RefreshCw, Plus, Trash2, KeyRound, DatabaseBackup, QrCode, Building, CreditCard, ShieldCheck } from "lucide-react";
import { companiesApi, usersApi, contractorsApi, pricingApi } from "@/services/api";
import { useAsync } from "@/hooks/useAsync";
import { fmtMoney, apiError } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { LoadingState, ErrorState, EmptyState } from "@/components/DataStates";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import BackupTab from "@/pages/settings/BackupTab";
import LabelTemplateTab from "@/pages/settings/LabelTemplateTab";
import PropertyManagementTab from "@/pages/settings/PropertyManagementTab";
import SubscriptionTab from "@/pages/settings/SubscriptionTab";
import PrivacyDataTab from "@/pages/settings/PrivacyDataTab";

const TABS = [
  { v: "company", label: "Company", icon: Building2 },
  { v: "subscription", label: "Subscription", icon: CreditCard },
  { v: "quote", label: "Quote & Invoice", icon: FileText },
  { v: "users", label: "Users", icon: UsersIcon },
  { v: "contractors", label: "Contractors", icon: HardHat },
  { v: "property", label: "Property Management", icon: Building },
  { v: "pricing", label: "Price log", icon: Tag },
  { v: "labels", label: "QR labels", icon: QrCode },
  { v: "backup", label: "Backup", icon: DatabaseBackup },
  { v: "privacy", label: "Privacy & data", icon: ShieldCheck },
];

export default function Settings() {
  const initialTab = (() => {
    const t = new URLSearchParams(window.location.search).get("tab");
    return TABS.some((x) => x.v === t) ? t : "company";
  })();
  return (
    <div className="space-y-6">
      <PageHeader title="Settings" subtitle="Company profile, templates, users, contractors and pricing" testId="settings-header" />
      <Tabs defaultValue={initialTab}>
        <TabsList className="flex w-full flex-wrap justify-start">
          {TABS.map((t) => (
            <TabsTrigger key={t.v} value={t.v} data-testid={`settings-tab-${t.v}`}>
              <t.icon className="mr-1.5 h-4 w-4" /> {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="company"><CompanyTab /></TabsContent>
        <TabsContent value="subscription"><SubscriptionTab /></TabsContent>
        <TabsContent value="quote"><QuoteInvoiceTab /></TabsContent>
        <TabsContent value="users"><UsersTab /></TabsContent>
        <TabsContent value="contractors"><ContractorsTab /></TabsContent>
        <TabsContent value="property"><PropertyManagementTab /></TabsContent>
        <TabsContent value="pricing"><PricingTab /></TabsContent>
        <TabsContent value="labels"><LabelTemplateTab /></TabsContent>
        <TabsContent value="backup"><BackupTab /></TabsContent>
        <TabsContent value="privacy"><PrivacyDataTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function useCompany() {
  return useAsync(() => companiesApi.me(), []);
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm">{label}</Label>
      {children}
    </div>
  );
}

function saveCompany(form, setBusy, reload, msg = "Saved") {
  setBusy(true);
  return companiesApi.update(form)
    .then(() => { toast.success(msg); reload && reload(); })
    .catch((e) => toast.error(apiError(e, "Could not save")))
    .finally(() => setBusy(false));
}

/* ---------------- Company ---------------- */
function CompanyTab() {
  const { data: c, loading, error, reload } = useCompany();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (c) setForm({
    name: c.name || "", address: c.address || "", postcode: c.postcode || "",
    maintenance_company_name: c.maintenance_company_name || "",
    maintenance_company_address: c.maintenance_company_address || "",
    maintenance_company_phone: c.maintenance_company_phone || "",
    maintenance_company_email: c.maintenance_company_email || "",
    maintenance_company_logo: c.maintenance_company_logo || "",
  }); }, [c]);

  if (loading) return <LoadingState label="Loading company…" />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!form) return null;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  return (
    <div className="max-w-2xl space-y-5">
      <Card className="space-y-4 p-5">
        <p className="font-semibold text-slate-800">Company profile</p>
        <Field label="Company name"><Input value={form.name} onChange={set("name")} data-testid="company-name" /></Field>
        <Field label="Address"><Textarea rows={2} value={form.address} onChange={set("address")} data-testid="company-address" /></Field>
        <Field label="Postcode"><Input value={form.postcode} onChange={set("postcode")} data-testid="company-postcode" /></Field>
      </Card>

      <Card className="space-y-4 p-5">
        <p className="font-semibold text-slate-800">Report & quote branding</p>
        <p className="text-sm text-slate-500">Shown on post-work reports, quotes and invoices.</p>
        <Field label="Trading name"><Input value={form.maintenance_company_name} onChange={set("maintenance_company_name")} data-testid="brand-name" /></Field>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Phone"><Input value={form.maintenance_company_phone} onChange={set("maintenance_company_phone")} data-testid="brand-phone" /></Field>
          <Field label="Email"><Input value={form.maintenance_company_email} onChange={set("maintenance_company_email")} data-testid="brand-email" /></Field>
        </div>
        <Field label="Address"><Textarea rows={2} value={form.maintenance_company_address} onChange={set("maintenance_company_address")} data-testid="brand-address" /></Field>
        <Field label="Logo">
          <div className="flex items-center gap-3">
            {form.maintenance_company_logo ? (
              <img src={form.maintenance_company_logo} alt="logo" className="h-14 w-auto rounded border border-slate-200 object-contain" />
            ) : <span className="text-sm text-slate-400">No logo</span>}
            <LogoUpload onChange={(b64) => setForm((f) => ({ ...f, maintenance_company_logo: b64 }))} onClear={() => setForm((f) => ({ ...f, maintenance_company_logo: "" }))} />
          </div>
        </Field>
      </Card>

      <JoinCodeCard company={c} reload={reload} />

      <div className="flex justify-end">
        <Button disabled={busy} onClick={() => saveCompany(form, setBusy, reload)} data-testid="company-save">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Save changes
        </Button>
      </div>
    </div>
  );
}

function LogoUpload({ onChange, onClear }) {
  const ref = React.useRef(null);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const dataUrl = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file); });
    onChange(dataUrl);
    e.target.value = "";
  };
  return (
    <div className="flex gap-2">
      <Button type="button" variant="outline" size="sm" onClick={() => ref.current?.click()} data-testid="brand-logo-upload">Upload</Button>
      <Button type="button" variant="ghost" size="sm" onClick={onClear}>Remove</Button>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={pick} />
    </div>
  );
}

function JoinCodeCard({ company, reload }) {
  const [code, setCode] = useState(company?.company_code || "");
  useEffect(() => setCode(company?.company_code || ""), [company]);
  const rotate = async () => {
    if (!window.confirm("Rotate the join code? The old code stops working.")) return;
    try { const r = await companiesApi.rotateCode(); setCode(r.data?.company_code || r.data?.code || ""); toast.success("Join code rotated"); reload && reload(); }
    catch (e) { toast.error(apiError(e, "Could not rotate")); }
  };
  return (
    <Card className="space-y-3 p-5">
      <p className="font-semibold text-slate-800">Team join code</p>
      <p className="text-sm text-slate-500">Share this code so staff can register into your company.</p>
      <div className="flex items-center gap-2">
        <code className="rounded bg-slate-100 px-3 py-1.5 text-lg font-bold tracking-wider text-slate-800" data-testid="join-code">{code || "—"}</code>
        <Button variant="ghost" size="sm" onClick={() => { navigator.clipboard?.writeText(code); toast.success("Copied"); }}><Copy className="h-4 w-4" /></Button>
        <Button variant="outline" size="sm" onClick={rotate} data-testid="rotate-code"><RefreshCw className="mr-1.5 h-4 w-4" /> Rotate</Button>
      </div>
    </Card>
  );
}

/* ---------------- Quote & Invoice ---------------- */
function QuoteInvoiceTab() {
  const { data: c, loading, error, reload } = useCompany();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (c) setForm({
    quote_display_mode: c.quote_display_mode || "labour_materials",
    quote_valid_days: c.quote_valid_days ?? 30,
    quote_payment_terms_days: c.quote_payment_terms_days ?? 30,
    quote_reinspection_price: c.quote_reinspection_price ?? 0,
    quote_reinspection_default_on: !!c.quote_reinspection_default_on,
    quote_show_door_photos: !!c.quote_show_door_photos,
    quote_include_compliance: c.quote_include_compliance !== false,
    quote_photo_appendix: !!c.quote_photo_appendix,
    quote_terms: c.quote_terms || "",
    quote_intro_wording: c.quote_intro_wording || "",
    quote_footer_wording: c.quote_footer_wording || "",
    quote_default_notes: c.quote_default_notes || "",
    invoice_header_wording: c.invoice_header_wording || "",
    invoice_intro_wording: c.invoice_intro_wording || "",
    invoice_footer_wording: c.invoice_footer_wording || "",
  }); }, [c]);

  if (loading) return <LoadingState label="Loading…" />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!form) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const num = (k) => (e) => set(k, e.target.value === "" ? "" : Number(e.target.value));

  return (
    <div className="max-w-2xl space-y-5">
      <Card className="space-y-4 p-5">
        <p className="font-semibold text-slate-800">Quote defaults</p>
        <Field label="Pricing display">
          <Select value={form.quote_display_mode} onValueChange={(v) => set("quote_display_mode", v)}>
            <SelectTrigger data-testid="quote-display-mode"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="labour_materials">Labour + materials breakdown</SelectItem>
              <SelectItem value="total_only">Total price only</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Field label="Valid for (days)"><Input type="number" value={form.quote_valid_days} onChange={num("quote_valid_days")} data-testid="quote-valid-days" /></Field>
          <Field label="Payment terms (days)"><Input type="number" value={form.quote_payment_terms_days} onChange={num("quote_payment_terms_days")} data-testid="quote-payment-terms" /></Field>
          <Field label="Re-inspection price (£)"><Input type="number" value={form.quote_reinspection_price} onChange={num("quote_reinspection_price")} data-testid="quote-reinspection-price" /></Field>
        </div>
        <ToggleRow label="Add re-inspection line by default" checked={form.quote_reinspection_default_on} onChange={(v) => set("quote_reinspection_default_on", v)} testId="quote-reinspection-default" />
        <ToggleRow label="Show door photos on quotes" checked={form.quote_show_door_photos} onChange={(v) => set("quote_show_door_photos", v)} testId="quote-show-photos" />
        <ToggleRow label="Include compliance summary" checked={form.quote_include_compliance} onChange={(v) => set("quote_include_compliance", v)} testId="quote-include-compliance" />
        <ToggleRow label="Photo appendix" checked={form.quote_photo_appendix} onChange={(v) => set("quote_photo_appendix", v)} testId="quote-photo-appendix" />
        <Field label="Intro wording"><Textarea rows={2} value={form.quote_intro_wording} onChange={(e) => set("quote_intro_wording", e.target.value)} data-testid="quote-intro" /></Field>
        <Field label="Terms & conditions"><Textarea rows={3} value={form.quote_terms} onChange={(e) => set("quote_terms", e.target.value)} data-testid="quote-terms" /></Field>
        <Field label="Footer wording"><Textarea rows={2} value={form.quote_footer_wording} onChange={(e) => set("quote_footer_wording", e.target.value)} data-testid="quote-footer" /></Field>
      </Card>

      <Card className="space-y-4 p-5">
        <p className="font-semibold text-slate-800">Invoice wording</p>
        <Field label="Header"><Input value={form.invoice_header_wording} onChange={(e) => set("invoice_header_wording", e.target.value)} data-testid="invoice-header" /></Field>
        <Field label="Intro"><Textarea rows={2} value={form.invoice_intro_wording} onChange={(e) => set("invoice_intro_wording", e.target.value)} data-testid="invoice-intro" /></Field>
        <Field label="Footer"><Textarea rows={2} value={form.invoice_footer_wording} onChange={(e) => set("invoice_footer_wording", e.target.value)} data-testid="invoice-footer" /></Field>
      </Card>

      <div className="flex justify-end">
        <Button disabled={busy} onClick={() => saveCompany(form, setBusy, reload)} data-testid="quote-settings-save">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Save changes
        </Button>
      </div>
    </div>
  );
}

function ToggleRow({ label, checked, onChange, testId }) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 px-3 py-2.5">
      <span className="text-sm text-slate-700">{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testId} />
    </div>
  );
}

/* ---------------- Users ---------------- */
function UsersTab() {
  const { data, loading, error, reload } = useAsync(() => usersApi.list(), []);
  const { user: me, refreshUser } = useAuth();
  const list = Array.isArray(data) ? data : [];

  const rename = async (u) => {
    const nm = window.prompt("Full name to show on reports (e.g. D Marsden):", u.name || "");
    if (nm === null) return;
    const trimmed = nm.trim();
    if (!trimmed) { toast.error("Name cannot be empty"); return; }
    if (trimmed === (u.name || "")) return;
    try {
      await usersApi.update(u.id, { name: trimmed });
      toast.success("Name updated — past & future reports now show this name");
      if (u.id === me?.id) { try { await refreshUser(); } catch { /* ignore */ } }
      reload();
    } catch (e) { toast.error(apiError(e, "Could not update name")); }
  };

  const setRole = async (u, is_company_admin) => {
    try { await usersApi.update(u.id, { is_company_admin }); toast.success("Role updated"); reload(); }
    catch (e) { toast.error(apiError(e, "Could not update")); }
  };
  const resetPw = async (u) => {
    const pw = window.prompt(`New password for ${u.email}:`);
    if (!pw) return;
    try { await usersApi.resetPassword(u.id, { new_password: pw }); toast.success("Password reset"); }
    catch (e) { toast.error(apiError(e, "Could not reset")); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.name || u.email}?`)) return;
    try { await usersApi.remove(u.id); toast.success("User removed"); reload(); }
    catch (e) { toast.error(apiError(e, "Could not remove")); }
  };

  if (loading) return <LoadingState label="Loading users…" />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  return (
    <div className="max-w-3xl space-y-3">
      {list.map((u) => (
        <Card key={u.id} className="flex flex-wrap items-center justify-between gap-3 p-4" data-testid={`user-row-${u.id}`}>
          <div className="min-w-0">
            <p className="truncate font-semibold text-slate-900">{u.name || u.email}{u.id === me?.id ? " (you)" : ""}</p>
            <p className="truncate text-sm text-slate-500">{u.email}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline" className={u.is_company_admin ? "border-primary/30 text-primary" : "text-slate-500"}>
              {u.is_platform_admin ? "Super admin" : u.is_company_admin ? "Admin" : (u.role || "inspector")}
            </Badge>
            <Button size="sm" variant="outline" onClick={() => rename(u)} data-testid={`user-rename-${u.id}`}>Edit name</Button>
            {u.id !== me?.id && !u.is_platform_admin && (
              <>
                <Button size="sm" variant="outline" onClick={() => setRole(u, !u.is_company_admin)} data-testid={`user-role-${u.id}`}>
                  {u.is_company_admin ? "Make inspector" : "Make admin"}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => resetPw(u)} data-testid={`user-reset-${u.id}`}><KeyRound className="h-4 w-4" /></Button>
                <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(u)} data-testid={`user-remove-${u.id}`}><Trash2 className="h-4 w-4" /></Button>
              </>
            )}
          </div>
        </Card>
      ))}
    </div>
  );
}

/* ---------------- Contractors ---------------- */
function ContractorsTab() {
  const { data, loading, error, reload } = useAsync(() => contractorsApi.list(), []);
  const list = Array.isArray(data) ? data : [];
  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.name}?`)) return;
    try { await contractorsApi.remove(c.id); toast.success("Deleted"); reload(); }
    catch (e) { toast.error(apiError(e, "Could not delete")); }
  };
  if (loading) return <LoadingState label="Loading contractors…" />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  return (
    <div className="max-w-3xl space-y-3">
      <div className="flex justify-end"><ContractorDialog onSaved={reload} /></div>
      {list.length === 0 ? (
        <EmptyState icon={HardHat} title="No contractors" message="Add contractors to brand post-work reports." />
      ) : list.map((c) => (
        <Card key={c.id} className="flex items-center justify-between gap-3 p-4" data-testid={`contractor-row-${c.id}`}>
          <div className="min-w-0">
            <p className="truncate font-semibold text-slate-900">{c.name}</p>
            <p className="truncate text-sm text-slate-500">{[c.phone, c.email].filter(Boolean).join(" · ") || "—"}</p>
          </div>
          <div className="flex items-center gap-2">
            <ContractorDialog contractor={c} onSaved={reload} />
            <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(c)} data-testid={`contractor-remove-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
          </div>
        </Card>
      ))}
    </div>
  );
}

function ContractorDialog({ contractor, onSaved }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "", logo: "" });
  useEffect(() => { if (open) setForm({ name: contractor?.name || "", phone: contractor?.phone || "", email: contractor?.email || "", address: contractor?.address || "", logo: contractor?.logo || "" }); }, [open, contractor]);
  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      if (contractor) await contractorsApi.update(contractor.id, form);
      else await contractorsApi.create(form);
      toast.success("Saved"); setOpen(false); onSaved();
    } catch (e) { toast.error(apiError(e, "Could not save")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {contractor
          ? <Button size="sm" variant="outline" data-testid={`contractor-edit-${contractor.id}`}>Edit</Button>
          : <Button data-testid="contractor-add"><Plus className="mr-1.5 h-4 w-4" /> Add contractor</Button>}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{contractor ? "Edit contractor" : "New contractor"}</DialogTitle>
          <DialogDescription>Contractors brand your post-work maintenance reports.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contractor-name" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Phone"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
            <Field label="Email"><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
          </div>
          <Field label="Address"><Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button disabled={busy} onClick={save} data-testid="contractor-save">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Pricing ---------------- */
function PricingTab() {
  const { data, loading, error, reload } = useAsync(() => pricingApi.list(), []);
  const list = Array.isArray(data) ? data : [];
  if (loading) return <LoadingState label="Loading pricing…" />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  return (
    <div className="max-w-3xl space-y-3">
      <p className="text-sm text-slate-500">{list.length} pricing item{list.length === 1 ? "" : "s"} in your library.</p>
      {list.length === 0 ? (
        <EmptyState icon={Tag} title="No pricing items" message="Your quote pricing library is empty." />
      ) : (
        <Card className="divide-y divide-slate-50">
          {list.map((p) => (
            <div key={p.id} className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`pricing-row-${p.id}`}>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-slate-800">{p.component || p.name || "Item"}</p>
                {(p.notes || p.reason) && <p className="truncate text-sm text-slate-500">{p.notes || p.reason}</p>}
              </div>
              <span className="shrink-0 font-semibold text-slate-800">{fmtMoney(p.default_cost ?? p.price ?? p.cost)}</span>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}
